import api from "../../../utils/api";
import type { FirestoreTimestamp } from "./leadsService";

// ============================================================================
// CRM Agents (Agentes) — frontend service. Talks to the torre-de-control-api
// crmAgents module. An agent is whoever owns leads / runs calls:
//   • type "human" — a sales rep (used for lead ownership)
//   • type "ai"    — an AI voice agent whose ElevenLabs config drives the
//                    automated call flow
//   • list   (authed): GET    /crm/agentsList   (with live lead counts)
//   • get    (authed): GET    /crm/agents?id=
//   • create (authed): POST   /crm/agents
//   • update (authed): PUT    /crm/agents?id=
//   • delete (authed): DELETE /crm/agents?id=
// ============================================================================

export type AgentType = "human" | "ai";
export type AgentStatus = "active" | "inactive";

/** What the AI agent is calling to do — picks a template + post-call behavior. */
export type AgentUseCase =
  | "confirm_lead"
  | "outbound_sales"
  | "product_intro"
  | "debt_collection"
  | "custom";

/** A single piece of information the agent must gather on the call. */
export interface DataField {
  key: string;
  label: string;
  required: boolean;
}

export interface Agent {
  id: string;
  name: string;
  type: AgentType;
  email: string | null;
  phone: string | null;
  role: string | null;
  status: AgentStatus;
  // The product this agent promotes (Coding Cloud / Wayak / Nuvex), used to tag
  // leads it captures on cold company calls. null when not tied to a product.
  product: string | null;
  // AI-only config (null/empty for humans). Drives the live call brain and
  // post-call extraction — see the torre-de-control-api claudeService.
  elevenLabsAgentId: string | null;
  voiceId: string | null;
  prompt: string | null; // extra/advanced instructions, appended to the script
  useCase: AgentUseCase | null;
  objective: string | null;
  openingLine: string | null;
  script: string | null;
  questions: string[];
  dataToCollect: DataField[];
  guardrails: string | null;
  tone: string | null;
  maxDurationMin: number | null;
  /** Ground this agent in the wiki/landing knowledge base (presentation/outbound). */
  useKnowledgeBase?: boolean;
  // ElevenLabs provisioning (server-managed). `elevenLabsManaged` is true for
  // agents we created via the API; `elevenLabsSync` is the last sync outcome.
  elevenLabsManaged?: boolean;
  elevenLabsSync?: ElevenLabsSync | null;
  /** Per-agent Google Calendar connection (mainly for human agents). */
  googleCalendar?: AgentGoogleCalendar | null;
  leadsCount: number;
  createdAt?: FirestoreTimestamp | string | null;
  updatedAt?: FirestoreTimestamp | string | null;
}

export interface ElevenLabsSync {
  status: "ok" | "error" | "pending";
  error: string | null;
  at?: string;
}

/** Per-agent Google Calendar link (safe fields only; the token never reaches the client). */
export interface AgentGoogleCalendar {
  connected: boolean;
  email: string | null;
  calendarId: string;
  createMeet: boolean;
  connectedAt?: string | null;
  sync?: { status: string; error: string | null; at?: string } | null;
}

/** A selectable ElevenLabs voice (from GET /crm/agents/voices). */
export interface VoiceOption {
  voiceId: string;
  name: string;
  category?: string | null;
}

/** An ElevenLabs ConvAI agent that exists in the workspace (importable). */
export interface ElevenLabsAgentSummary {
  agentId: string;
  name: string;
  imported: boolean;
  crmAgentId: string | null;
}

/** Fields a console user can set when creating / editing an agent. */
export type AgentDraft = Pick<
  Agent,
  | "name"
  | "type"
  | "email"
  | "phone"
  | "role"
  | "status"
  | "product"
  | "elevenLabsAgentId"
  | "voiceId"
  | "prompt"
  | "useCase"
  | "objective"
  | "openingLine"
  | "script"
  | "questions"
  | "dataToCollect"
  | "guardrails"
  | "tone"
  | "maxDurationMin"
  | "useKnowledgeBase"
>;

export interface AgentsPage {
  data: Agent[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

interface AgentsListParams {
  page?: number;
  limit?: number;
  type?: AgentType;
  status?: AgentStatus;
}

export async function fetchAgents(params: AgentsListParams = {}): Promise<AgentsPage> {
  const { page = 1, limit = 200, type, status } = params;
  const res = await api.get("/crm/agentsList", {
    params: { page, limit, ...(type ? { type } : {}), ...(status ? { status } : {}) },
  });
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? page,
  };
}

// The backend `response()` helper wraps single-item payloads as
//   { meta, data: <payload> }  and our controllers pass  { message, data: X },
// so the actual value sits at res.data.data.data. (List endpoints use
// pageResponse → the array is at res.data.data, see fetchAgents above.)
function unwrap<T>(res: { data?: { data?: { data?: T } } }): T | undefined {
  return res.data?.data?.data;
}

/** Los vendedores a los que se les puede asignar algo: personas activas. */
export function assignableAgents(agents: Agent[]): Agent[] {
  return agents
    .filter((a) => a.type === "human" && a.status !== "inactive")
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

/** Cuenta de una colección en la pasada única del responsable por producto. */
export interface OwnerApplyCount {
  unassigned: number;
  assigned: number;
  skipped: number;
  byProduct: Record<string, number>;
}

export interface OwnerApplyResult {
  dryRun: boolean;
  leads: OwnerApplyCount;
  deals: OwnerApplyCount;
}

/**
 * Reparte los leads y negocios que ya existen y no tienen responsable. Con
 * `dryRun` (por omisión) solo cuenta. Nunca cambia a un responsable ya puesto.
 */
export async function applyOwnerAssignment(dryRun = true): Promise<OwnerApplyResult> {
  const res = await api.post("/crm/agents/assignment/apply", { dryRun });
  return res.data?.data?.data;
}

export async function createAgent(draft: AgentDraft): Promise<Agent> {
  const res = await api.post(`/crm/agents`, draft);
  return unwrap<Agent>(res) as Agent;
}

export async function updateAgent(id: string, patch: Partial<AgentDraft>): Promise<Agent> {
  const res = await api.put(`/crm/agents`, patch, { params: { id } });
  return unwrap<Agent>(res) as Agent;
}

export async function deleteAgent(id: string): Promise<void> {
  await api.delete(`/crm/agents`, { params: { id } });
}

/** Available ElevenLabs voices for the agent voice picker. Returns [] if EL is
 *  not configured or the endpoint isn't deployed. */
export async function fetchVoices(): Promise<VoiceOption[]> {
  try {
    const res = await api.get("/crm/agents/voices");
    const arr = unwrap<VoiceOption[]>(res);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/** ElevenLabs ConvAI agents in the workspace, flagged if already imported. */
export async function fetchElevenLabsAgents(): Promise<ElevenLabsAgentSummary[]> {
  const res = await api.get("/crm/agents/elevenlabs");
  const arr = unwrap<ElevenLabsAgentSummary[]>(res);
  return Array.isArray(arr) ? arr : [];
}

/** Import an existing ElevenLabs agent into Agentes as an editable record. */
export async function importElevenLabsAgent(elevenLabsAgentId: string): Promise<Agent> {
  const res = await api.post("/crm/agents/import", { elevenLabsAgentId });
  return unwrap<Agent>(res) as Agent;
}

/** The best AI agent to call a given lead, with a short human reason. */
export interface AgentSuggestion {
  agentId: string | null;
  agent: Agent | null;
  reason: string;
  ranked: { agentId: string; score: number; reasons: string[] }[];
}

/** Ask the backend which AI agent best fits a lead (pre-selects the picker). */
export async function suggestAgentForLead(leadId: string): Promise<AgentSuggestion> {
  const res = await api.get("/crm/agents/suggest", { params: { leadId } });
  const data = unwrap<AgentSuggestion>(res);
  return data ?? { agentId: null, agent: null, reason: "", ranked: [] };
}

// ===========================================================================
// Per-agent Google Calendar connect (OAuth) — backs the Reuniones page.
//   • auth url   GET  /crm/google/auth-url?agentId=   → consent URL (open popup)
//   • status     GET  /crm/google/status?agentId=
//   • disconnect POST /crm/google/disconnect?agentId=
//   • events     GET  /crm/google/events?agentId=&from=&to=  (read-only busy)
// ===========================================================================

export interface AgentGoogleStatus {
  configured: boolean;
  connected: boolean;
  email: string | null;
  calendarId: string;
  createMeet: boolean;
  connectedAt?: string | null;
  sync?: { status: string; error: string | null; at?: string } | null;
  oauthConfigured?: boolean;
}

export interface GoogleBusyEvent {
  id: string;
  summary: string;
  start: string | null;
  end: string | null;
  allDay: boolean;
  htmlLink: string | null;
  status: string | null;
}

/** Build the Google consent URL for an agent (open it in a popup to connect). */
export async function getAgentGoogleAuthUrl(agentId: string): Promise<string> {
  const res = await api.get("/crm/google/auth-url", { params: { agentId } });
  return res.data?.data?.data?.url ?? "";
}

/** Connection status for an agent's Google Calendar. */
export async function getAgentGoogleStatus(agentId: string): Promise<AgentGoogleStatus> {
  const res = await api.get("/crm/google/status", { params: { agentId } });
  return (
    res.data?.data?.data ?? {
      configured: false,
      connected: false,
      email: null,
      calendarId: "primary",
      createMeet: true,
    }
  );
}

/** Disconnect an agent's Google Calendar (clears the stored token). */
export async function disconnectAgentGoogle(agentId: string): Promise<void> {
  await api.post("/crm/google/disconnect", {}, { params: { agentId } });
}

/** The agent's existing Google events for a range (read-only busy display). */
export async function fetchAgentGoogleEvents(
  agentId: string,
  from: string,
  to: string,
): Promise<GoogleBusyEvent[]> {
  const res = await api.get("/crm/google/events", { params: { agentId, from, to } });
  const items = res.data?.data?.data?.items;
  return Array.isArray(items) ? items : [];
}
